'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { usePublicAuthor, usePublicAuthors } from '@/hooks/use-public-authors';
import { useTitlesForAuthor } from '@/hooks/use-public-titles';
import { ROUTES } from '@/lib/routes';
import { PageIntro } from '@/components/platform/page-intro';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function getAuthorInitials(name: string) {
 const parts = name.trim().split(/\s+/).filter(Boolean);
 if (!parts.length) return '?';
 return parts
 .map((part) => part[0]?.toUpperCase())
 .filter(Boolean)
 .join('')
 .slice(0, 2);
}

function AuthorProfileImage({
 src,
 alt,
 fallback,
 size = 'h-14 w-14',
}: {
 src?: string;
 alt: string;
 fallback: string;
 size?: string;
}) {
 const [error, setError] = useState(false);

 if (!src || error) {
 return (
 <div className={`flex ${size} items-center justify-center rounded-full border border-blush/70 bg-cream text-sm font-semibold text-charcoal`}>
 {getAuthorInitials(fallback)}
 </div>
 );
 }

 return (
 <div className={`relative ${size} overflow-hidden rounded-full border border-blush/70 bg-cream`}>
 <Image src={src} alt={alt} fill className="h-full w-full object-cover" onError={() => setError(true)} />
 </div>
 );
}

export function PublicAuthorsPanel() {
 const { authors, loading, error } = usePublicAuthors();

 return (
 <section className="bg-cream py-20">
 <div className="mx-auto max-w-6xl px-4">
 <PageIntro eyebrow="Author directory" title="Meet the authors" description="This route is now connected to public authorProfiles so the directory can go live before the full marketplace rollout is finished.">
 <div className="mt-4 flex flex-wrap gap-3">
 <Button asChild className="bg-[#5F8769] text-white hover:bg-[#547A5F]"><Link href={ROUTES.luminaPress}>Discover Lumina Press</Link></Button>
 <Button asChild variant="outline" className="border-blush bg-cream text-charcoal hover:bg-blush/60"><Link href={ROUTES.titles}>Published titles</Link></Button>
 </div>
 </PageIntro>

 {loading ? <p className="mt-8 text-sm text-charcoal/70">Loading public authors...</p> : error ? <p className="mt-8 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : authors.length ? (
 <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
 {authors.map((author) => (
 <Card key={author.authorId} className="rounded-[2rem] border border-blush/70 bg-white shadow-sm">
 <CardHeader>
 <div className="flex items-start gap-4">
 <AuthorProfileImage src={author.avatarUrl} alt={`${author.penName} profile`} fallback={author.penName} />
 <div>
 <CardTitle className="font-headline text-2xl text-charcoal">{author.penName}</CardTitle>
 <CardDescription className="text-charcoal/70">{author.bioShort || 'Public author profile is live, but the short bio has not been filled yet.'}</CardDescription>
 </div>
 </div>
 </CardHeader>
 <CardContent className="flex flex-wrap gap-3">
 <Button asChild className="bg-[#5F8769] text-white hover:bg-[#547A5F]"><Link href={`/authors/${author.slug}`}>View profile</Link></Button>
 {author.websiteUrl ? <Button asChild variant="outline" className="border-blush bg-cream text-charcoal hover:bg-blush/60"><Link href={author.websiteUrl}>Website</Link></Button> : null}
 </CardContent>
 </Card>
 ))}
 </div>
 ) : (
 <Card className="mt-8 rounded-[2rem] border border-blush/70 bg-white shadow-sm">
 <CardContent className="p-6 text-sm text-charcoal/75">No public authors yet. Author profiles appear here after a user completes author onboarding and enables public visibility in Account &gt; Profile.</CardContent>
 </Card>
 )}
 </div>
 </section>
 );
}

export function PublicAuthorProfilePanel({ authorSlug }: { authorSlug: string }) {
 const { author, loading, error } = usePublicAuthor(authorSlug);
 const { titles, loading: titlesLoading } = useTitlesForAuthor(author?.authorId);

 return (
 <section className="bg-cream py-20">
 <div className="mx-auto max-w-5xl px-4">
 {loading ? <p className="text-sm text-charcoal/70">Loading author profile...</p> : error ? <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : author ? (
 <div className="space-y-8">
 <Card className="rounded-[2rem] border border-blush/70 bg-white shadow-sm">
 <CardHeader>
 <p className="text-xs font-semibold uppercase tracking-[0.24em] text-terra">Author profile</p>
 <div className="mt-3 flex flex-wrap gap-4">
 <AuthorProfileImage src={author.avatarUrl} alt={`${author.penName} profile`} fallback={author.penName} size="h-20 w-20" />
 <div>
 <CardTitle className="font-headline text-4xl text-charcoal">{author.penName}</CardTitle>
 <CardDescription className="max-w-2xl text-base leading-7 text-charcoal/75">
 {author.bioLong || author.bioShort || 'This author profile is live, but a longer public biography has not been added yet.'}
 </CardDescription>
 </div>
 </div>
 <div className="grid gap-4 md:grid-cols-2">
 {author.websiteUrl ? <div className="rounded-2xl border border-blush/70 bg-cream px-4 py-3 text-sm text-charcoal/80">Website: {author.websiteUrl}</div> : null}
 {author.instagramUrl ? <div className="rounded-2xl border border-blush/70 bg-cream px-4 py-3 text-sm text-charcoal/80">Instagram: {author.instagramUrl}</div> : null}
 {author.amazonAuthorUrl ? <div className="rounded-2xl border border-blush/70 bg-cream px-4 py-3 text-sm text-charcoal/80">Amazon author page: {author.amazonAuthorUrl}</div> : null}
 <div className="rounded-2xl border border-blush/70 bg-cream px-4 py-3 text-sm text-charcoal/80">Profile slug: {author.slug}</div>
 </div>
 <div className="mt-4 flex flex-wrap gap-3">
 <Button asChild className="bg-[#5F8769] text-white hover:bg-[#547A5F]"><Link href={ROUTES.authors}>Back to authors</Link></Button>
 <Button asChild variant="outline" className="border-blush bg-cream text-charcoal hover:bg-blush/60"><Link href={ROUTES.titles}>Browse published titles</Link></Button>
 </div>
 </CardHeader>
 <CardContent>
 {titlesLoading ? <p className="text-sm text-charcoal/70">Loading published titles...</p> : titles.length ? (
 <div className="grid gap-4 md:grid-cols-2">
 {titles.map((title) => (
 <div key={title.titleId} className="rounded-2xl border border-blush/70 bg-cream p-4">
 <p className="font-medium text-charcoal">{title.title}</p>
 <p className="mt-1 text-sm text-charcoal/70">{title.shortDescription || title.subtitle || title.category}</p>
 <div className="mt-3 flex items-center justify-between text-sm text-charcoal/70">
 <span>{title.currency} {Number(title.price || 0).toFixed(2)}</span>
 <Link href={`/titles/${title.slug}`} className="font-medium text-terra underline underline-offset-2">Open title</Link>
 </div>
 </div>
 ))}
 </div>
 ) : <p className="text-sm text-charcoal/70">This author does not have any published storefront titles yet.</p>}
 </CardContent>
 </Card>
 </div>
 ) : (
 <Card className="rounded-[2rem] border border-blush/70 bg-white shadow-sm"><CardContent className="p-6 text-sm text-charcoal/75">This author profile is not public or does not exist yet.</CardContent></Card>
 )}
 </div>
 </section>
 );
}
